import React, { useState, useEffect } from 'react';
import AdminBackLink from '../components/AdminBackLink';

import axiosInstance from '../services/api';
import api from '../config/api';
import { Consultation, ConsultationType, ConsultationStatus, User } from '../types';
import { HiOutlineTrash } from 'react-icons/hi';

const Consultations: React.FC = () => {
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const [updateForm, setUpdateForm] = useState({
    status: ConsultationStatus.PENDING,
    notes: '',
    assignedTo: undefined as number | undefined,
  });

  useEffect(() => {
    fetchConsultations();
    fetchUsers();
  }, [page, filterStatus]);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      setError('');
      const status = filterStatus || undefined;
      const response = await axiosInstance.get(api.consultations.getAll(status, page, limit));
      setConsultations(response.data.consultations || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحميل الاستشارات');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get(api.auth.getAllUsers);
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleViewDetails = (consultation: Consultation) => {
    setSelectedConsultation(consultation);
    setUpdateForm({
      status: consultation.status,
      notes: consultation.notes || '',
      assignedTo: consultation.assignedTo,
    });
    setShowDetailsModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedConsultation) return;

    try {
      setError('');
      setSuccess('');
      const updateData: any = {
        status: updateForm.status,
        notes: updateForm.notes,
      };
      if (updateForm.assignedTo) {
        updateData.assignedTo = updateForm.assignedTo;
      } else {
        updateData.assignedTo = null;
      }

      await axiosInstance.put(api.consultations.update(selectedConsultation.id), updateData);
      setSuccess('تم تحديث الاستشارة بنجاح');
      setShowDetailsModal(false);
      fetchConsultations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل تحديث الاستشارة');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه الاستشارة؟')) return;

    try {
      setError('');
      setSuccess('');
      await axiosInstance.delete(api.consultations.delete(id));
      setSuccess('تم حذف الاستشارة بنجاح');
      fetchConsultations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'فشل حذف الاستشارة');
    }
  };

  const getStatusBadgeClass = (status: ConsultationStatus) => {
    switch (status) {
      case ConsultationStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case ConsultationStatus.IN_PROGRESS:
        return 'bg-blue-100 text-blue-800';
      case ConsultationStatus.COMPLETED:
        return 'bg-green-100 text-green-800';
      case ConsultationStatus.CANCELLED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: ConsultationStatus) => {
    switch (status) {
      case ConsultationStatus.PENDING:
        return 'قيد الانتظار';
      case ConsultationStatus.IN_PROGRESS:
        return 'قيد المعالجة';
      case ConsultationStatus.COMPLETED:
        return 'مكتملة';
      case ConsultationStatus.CANCELLED:
        return 'ملغاة';
      default:
        return status;
    }
  };

  const getTypeLabel = (type: ConsultationType) => {
    switch (type) {
      case ConsultationType.PHONE:
        return 'مكالمة تليفونية';
      case ConsultationType.OFFICE:
        return 'مكتب';
      case ConsultationType.ONLINE:
        return 'اجتماع أونلاين';
      default:
        return type;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--background-paper)' }}>
      <nav
        style={{
          backgroundColor: '#041525',
          borderBottom: '2px solid #477CD7',
          width: '100%',
          height: '72px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between items-center h-full relative">
            <AdminBackLink />
            <h1 
              className="text-xl font-semibold text-white absolute left-1/2 transform -translate-x-1/2"
              style={{
                fontFamily: 'var(--font-arabtype)',
                letterSpacing: '0.5px',
              }}
            >
              الاستشارات
            </h1>
            <div className="w-24"></div>
          </div>
        </div>
      </nav>

      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          {/* Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg">
              {success}
            </div>
          )}

          {/* Filters */}
          <div className="mb-6 flex gap-4 items-center">
            <label className="font-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>فلترة حسب الحالة:</label>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
              style={{
                borderColor: 'var(--border-main)',
                '--tw-ring-color': 'var(--navbar-border)',
              } as React.CSSProperties}
            >
              <option value="">الكل</option>
              <option value={ConsultationStatus.PENDING}>قيد الانتظار</option>
              <option value={ConsultationStatus.IN_PROGRESS}>قيد المعالجة</option>
              <option value={ConsultationStatus.COMPLETED}>مكتملة</option>
              <option value={ConsultationStatus.CANCELLED}>ملغاة</option>
            </select>
            <div style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              إجمالي: {total} استشارة
            </div>
          </div>

          {/* Cards */}
          {loading ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>جاري التحميل...</div>
          ) : consultations.length === 0 ? (
            <div className="text-center py-12" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>لا توجد استشارات</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {consultations.map((consultation) => (
                  <div
                    key={consultation.id}
                    className="p-6 rounded-lg transition-all"
                    style={{
                      background: 'rgba(8, 42, 73, 0.6)',
                      border: '1px solid transparent',
                      color: '#ffffff',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(104, 148, 222, 0.5)';
                      e.currentTarget.style.boxShadow = '0 0 20px rgba(104, 148, 222, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold mb-2">{consultation.name}</h3>
                      <div className="space-y-2">
                        <div>
                          <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>البريد:</span>
                          <p className="text-sm mt-1" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{consultation.email}</p>
                        </div>
                        <div>
                          <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>نوع الاستشارة:</span>
                          <p className="text-sm mt-1 font-medium">{getTypeLabel(consultation.consultationType)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>المدة:</span>
                          <span className="text-sm font-medium">{consultation.duration} دقيقة</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>التاريخ:</span>
                          <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{formatDate(consultation.createdAt)}</span>
                        </div>
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full w-fit ${getStatusBadgeClass(
                            consultation.status
                          )}`}
                          style={{
                            backgroundColor: consultation.status === ConsultationStatus.COMPLETED 
                              ? 'rgba(34, 197, 94, 0.2)' 
                              : consultation.status === ConsultationStatus.PENDING
                              ? 'rgba(234, 179, 8, 0.2)'
                              : consultation.status === ConsultationStatus.IN_PROGRESS
                              ? 'rgba(59, 130, 246, 0.2)'
                              : 'rgba(239, 68, 68, 0.2)',
                            color: consultation.status === ConsultationStatus.COMPLETED 
                              ? '#86efac' 
                              : consultation.status === ConsultationStatus.PENDING
                              ? '#fde047'
                              : consultation.status === ConsultationStatus.IN_PROGRESS
                              ? '#93c5fd'
                              : '#fca5a5',
                          }}
                        >
                          {getStatusLabel(consultation.status)}
                        </span>
                      </div>
                      <div className="flex gap-2 items-center">
                        <button
                          onClick={() => handleViewDetails(consultation)}
                          className="px-3 py-2 rounded-lg transition-all text-sm font-medium"
                          style={{ 
                            background: 'rgba(71, 124, 215, 0.2)',
                            color: '#6894DE',
                            border: '1px solid rgba(71, 124, 215, 0.3)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(71, 124, 215, 0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(71, 124, 215, 0.2)';
                          }}
                        >
                          عرض
                        </button>
                        <button
                          onClick={() => handleDelete(consultation.id)}
                          className="p-2 rounded-lg transition-all hover:bg-red-500 hover:bg-opacity-20"
                          style={{ color: '#EF4444' }}
                          title="حذف"
                        >
                          {React.createElement(HiOutlineTrash as any, { size: 18 })}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex justify-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    style={{
                      background: page === 1 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(71, 124, 215, 0.2)',
                      color: '#ffffff',
                      border: '1px solid rgba(71, 124, 215, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      if (page !== 1) {
                        e.currentTarget.style.background = 'rgba(71, 124, 215, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== 1) {
                        e.currentTarget.style.background = 'rgba(71, 124, 215, 0.2)';
                      }
                    }}
                  >
                    السابق
                  </button>
                  <span className="px-4 py-2" style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    صفحة {page} من {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    style={{
                      background: page === totalPages ? 'rgba(255, 255, 255, 0.1)' : 'rgba(71, 124, 215, 0.2)',
                      color: '#ffffff',
                      border: '1px solid rgba(71, 124, 215, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      if (page !== totalPages) {
                        e.currentTarget.style.background = 'rgba(71, 124, 215, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (page !== totalPages) {
                        e.currentTarget.style.background = 'rgba(71, 124, 215, 0.2)';
                      }
                    }}
                  >
                    التالي
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedConsultation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">تفاصيل الاستشارة</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Consultation Details */}
              <div className="space-y-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">الاسم</label>
                    <p className="text-gray-900">{selectedConsultation.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">البريد الإلكتروني</label>
                    <p className="text-gray-900">{selectedConsultation.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
                    <p className="text-gray-900">{selectedConsultation.phone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">نوع الاستشارة</label>
                    <p className="text-gray-900">{getTypeLabel(selectedConsultation.consultationType)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">مدة الاستشارة</label>
                    <p className="text-gray-900">{selectedConsultation.duration} دقيقة</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الطلب</label>
                    <p className="text-gray-900">{formatDate(selectedConsultation.createdAt)}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الرسالة</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">
                    {selectedConsultation.message || 'لا توجد رسالة'}
                  </p>
                </div>
              </div>

              {/* Update Form */}
              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">تحديث الاستشارة</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الحالة
                  </label>
                  <select
                    value={updateForm.status}
                    onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value as ConsultationStatus })}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{
                      borderColor: 'var(--border-main)',
                    } as React.CSSProperties}
                  >
                    <option value={ConsultationStatus.PENDING}>قيد الانتظار</option>
                    <option value={ConsultationStatus.IN_PROGRESS}>قيد المعالجة</option>
                    <option value={ConsultationStatus.COMPLETED}>مكتملة</option>
                    <option value={ConsultationStatus.CANCELLED}>ملغاة</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    تعيين لمستخدم
                  </label>
                  <select
                    value={updateForm.assignedTo || ''}
                    onChange={(e) => setUpdateForm({ ...updateForm, assignedTo: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{
                      borderColor: 'var(--border-main)',
                    } as React.CSSProperties}
                  >
                    <option value="">غير معين</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.email} ({user.role})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الملاحظات
                  </label>
                  <textarea
                    value={updateForm.notes}
                    onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
                    style={{
                      borderColor: 'var(--border-main)',
                    } as React.CSSProperties}
                    placeholder="أضف ملاحظات هنا..."
                  />
                </div>

                <div className="flex gap-4 justify-end pt-4">
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleUpdate}
                    className="px-6 py-2 rounded-lg text-white transition-opacity hover:opacity-90"
                    style={{
                      background: 'var(--button-gradient)',
                    }}
                  >
                    حفظ التغييرات
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Consultations;

